import { useEffect, useState } from 'react';

export default function ServiceWorkerErrorHandler() {
  const [showError, setShowError] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      const message = event.message || event.error?.message || '';
      
      // Detectar el error específico del service worker
      if (message.includes('message channel closed') || 
          message.includes('listener indicated an asynchronous response') ||
          message.includes('__name is not defined') ||
          message.includes('Failed to fetch dynamically imported module') ||
          message.includes('Loading chunk')) {
        console.error('[SW Error Detected]:', message);
        setShowError(true);
      }
    };

    const promiseRejectionHandler = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason);
      
      if (message.includes('message channel closed') || 
          message.includes('listener indicated an asynchronous response') ||
          message.includes('__name is not defined') ||
          message.includes('Failed to fetch dynamically imported module') ||
          message.includes('Loading chunk')) {
        console.error('[SW Promise Rejection]:', message);
        setShowError(true);
      }
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', promiseRejectionHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', promiseRejectionHandler);
    };
  }, []);

  const handleClearServiceWorkers = async () => {
    setClearing(true);
    
    try {
      if (!('serviceWorker' in navigator)) {
        alert('Tu navegador no soporta service workers');
        setClearing(false);
        return;
      }

      const registrations = await navigator.serviceWorker.getRegistrations();
      
      for (const registration of registrations) {
        await registration.unregister();
      }

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      // Recargar la página después de limpiar
      window.location.reload();
    } catch (error) {
      console.error('Error clearing service workers:', error);
      alert('Hubo un error al limpiar. Intenta manualmente desde DevTools > Application > Service Workers');
      setClearing(false);
    }
  };

  const handleDismiss = () => {
    setShowError(false);
    // Guardar en localStorage para no mostrar de nuevo en esta sesión
    sessionStorage.setItem('sw-error-dismissed', 'true');
  };

  // No mostrar si ya fue descartado en esta sesión
  if (sessionStorage.getItem('sw-error-dismissed') === 'true') {
    return null;
  }

  if (!showError) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-md animate-in slide-in-from-bottom duration-300">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border-2 border-yellow-400 dark:border-yellow-600 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-2xl">⚠️</div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 dark:text-white mb-1">
              Error de Service Worker Detectado
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Hay un problema con el cache de la aplicación. Limpia los service workers para resolverlo.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleClearServiceWorkers}
                disabled={clearing}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {clearing ? 'Limpiando...' : '🧹 Limpiar Ahora'}
              </button>
              <button
                onClick={handleDismiss}
                disabled={clearing}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              >
                Ignorar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
